import type { CreativeTemplate } from "@mw/shared/payload-types";

import type { FlowTemplateResultType } from "@/app/_api/flowTemplatesServer";

export type WorkflowInputType = "file" | "text";
// Aliases the data-layer type (type-only import, erased at compile time) so the
// two layers can't drift; the view owns the name, `_api` owns the definition.
export type WorkflowResultType = FlowTemplateResultType;
export type GenerationModel = NonNullable<CreativeTemplate["generationModel"]>;
export const DEFAULT_GENERATION_MODEL: GenerationModel = "gemini-2.5-flash-image";

export interface WorkflowInputConfig {
  inputType: WorkflowInputType;
  acceptedMimeTypes: string;
  resultType: WorkflowResultType;
  maxFileSizeMb: number;
}

/**
 * Maps CMS workflowSlug values to their input/output configurations.
 * The CMS workflowSlug determines what kind of input the omnibox shows.
 */
type WorkflowSlug = CreativeTemplate["workflowSlug"];

export const WORKFLOW_INPUT_CONFIG: Record<WorkflowSlug, WorkflowInputConfig> = {
  "edit-image": {
    inputType: "file",
    acceptedMimeTypes: "image/*",
    resultType: "image",
    maxFileSizeMb: 20,
  },
  "text-to-image": {
    inputType: "text",
    acceptedMimeTypes: "",
    resultType: "image",
    maxFileSizeMb: 0,
  },
  "lip-sync": {
    inputType: "file",
    acceptedMimeTypes: "image/*",
    resultType: "video",
    maxFileSizeMb: 20,
  },
  "edit-video": {
    inputType: "file",
    acceptedMimeTypes: "video/*",
    resultType: "video",
    maxFileSizeMb: 100,
  },
  "image-to-video": {
    inputType: "file",
    acceptedMimeTypes: "image/*",
    resultType: "video",
    maxFileSizeMb: 20,
  },
};

export type ContentWorkflowStep =
  | "idle"
  | "file_selected"
  | "uploading"
  | "generating"
  | "result"
  | "quota_exceeded"
  | "rate_limited";

export function isProcessingStep(step: ContentWorkflowStep): boolean {
  return step === "uploading" || step === "generating";
}

export function isResultStep(step: ContentWorkflowStep): boolean {
  return step === "result";
}

export function isRateLimitedStep(step: ContentWorkflowStep) {
  return step === "rate_limited" || step === "quota_exceeded";
}

export interface BreadcrumbLink {
  title: string;
  href?: string;
}

export const MODEL_DISPLAY_NAMES: Record<GenerationModel, string> = {
  "gemini-2.5-flash-image": "Nano Banana",
  "gemini-3.1-flash-image-preview": "Nano Banana 2",
  "gemini-3-pro-image-preview": "Nano Banana Pro",
  "gpt-image-2": "GPT Image 2",
  "kling-3-pro": "Kling 3 Pro",
  "kling-omni-edit-video": "Kling Omni",
  "veo-3.1-generate-001": "Veo 3.1",
  "veo-3.1-fast-generate-001": "Veo 3.1 Fast",
  "topaz-image-upscale": "Topaz",
  "topaz-video-upscale": "Topaz",
  "ltx-v2-retake": "LTX V2",
  "creatify-aurora": "Creatify Aurora",
  "flux-1-kontext": "FLUX.1 Kontext Pro",
  "flux-2-pro": "FLUX.2 Pro",
  "bytedance-seedance-v2": "Seedance 2.0",
  "sync-lipsync-v2-pro": "Sync Lipsync",
  "birefnet-v2-bg-removal": "Background Removal",
};

export function getModelDisplayName(modelId: GenerationModel): string {
  return MODEL_DISPLAY_NAMES[modelId];
}

export const MODEL_OUTPUT_TYPE: Record<GenerationModel, WorkflowResultType> = {
  "gemini-2.5-flash-image": "image",
  "bytedance-seedance-v2": "video",
  "gemini-3.1-flash-image-preview": "image",
  "gemini-3-pro-image-preview": "image",
  "gpt-image-2": "image",
  "kling-3-pro": "video",
  "kling-omni-edit-video": "video",
  "veo-3.1-generate-001": "video",
  "veo-3.1-fast-generate-001": "video",
  "topaz-image-upscale": "image",
  "topaz-video-upscale": "video",
  "ltx-v2-retake": "video",
  "creatify-aurora": "video",
  "flux-1-kontext": "image",
  "flux-2-pro": "image",
  "sync-lipsync-v2-pro": "video",
  "birefnet-v2-bg-removal": "image",
};
